import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Compliance from "@/pages/Compliance";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Checklists from "@/pages/Checklists";
import ChecklistBuilder from "@/pages/ChecklistBuilder";
import ChecklistRun from "@/pages/ChecklistRun";
import Teams from "@/pages/Teams";
import WorkOrders from "@/pages/WorkOrders";
import Reports from "@/pages/Reports";
import Meetings from "@/pages/Meetings";
import FormalMeetingRoom from "@/pages/FormalMeetingRoom";
import MeetingAttendGate from "@/pages/MeetingAttendGate";
import MeetingGuestAttend from "@/pages/MeetingGuestAttend";
import MeetingRoom from "@/pages/MeetingRoom";
import Actions from "@/pages/Actions";
import ActionDetail from "@/pages/ActionDetail";
import VerificationValidation from "@/pages/VerificationValidation";
import VerificationValidationDetail from "@/pages/VerificationValidationDetail";
import ComplianceCoverage from "@/pages/ComplianceCoverage";
import SopsAndDocs from "@/pages/SopsAndDocs";
import OrgDev from "@/pages/OrgDev";
import Admin from "@/pages/Admin";
import MasterDashboard from "@/pages/MasterDashboard";
import MasterLibrary from "@/pages/MasterLibrary";
import MasterOrgs from "@/pages/MasterOrgs";
import MyAnalytics from "@/pages/MyAnalytics";
import HubPulse from "@/pages/HubPulse";
import TracePage from "@/pages/TracePage";
import AppShell from "@/components/layout/AppShell";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 font-mono text-navy">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-center" richColors offset={88} />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/meeting/:id/guest" element={<MeetingGuestAttend />} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/inbox" element={<Navigate to="/app" replace />} />
          <Route path="/app/tasks" element={<Navigate to="/app/actions" replace />} />
          <Route path="/app/checklists" element={<Protected><Checklists /></Protected>} />
          <Route path="/app/checklists/builder/:id?" element={<Protected><ChecklistBuilder /></Protected>} />
          <Route path="/app/checklists/run/:id" element={<Protected><ChecklistRun /></Protected>} />
          <Route path="/app/teams" element={<Protected><Teams /></Protected>} />
          <Route path="/app/work-orders" element={<Protected><WorkOrders /></Protected>} />
          <Route path="/app/reports" element={<Protected><Reports /></Protected>} />
          <Route path="/app/meetings" element={<Protected><Meetings /></Protected>} />
          <Route path="/app/meetings/:id/attend" element={<Protected><MeetingAttendGate /></Protected>} />
          <Route path="/app/meetings/:id" element={<Protected><FormalMeetingRoom /></Protected>} />
          <Route path="/app/close-out-meeting/:id" element={<Protected><MeetingRoom /></Protected>} />
          <Route path="/app/calendar" element={<Navigate to="/app" replace />} />
          <Route path="/app/actions" element={<Protected><Actions /></Protected>} />
          <Route path="/app/actions/:id" element={<Protected><ActionDetail /></Protected>} />
          <Route path="/app/vv" element={<Protected><VerificationValidation /></Protected>} />
          <Route path="/app/vv/:id" element={<Protected><VerificationValidationDetail /></Protected>} />
          <Route path="/app/coverage" element={<Protected><ComplianceCoverage /></Protected>} />
          <Route path="/app/sops" element={<Protected><SopsAndDocs /></Protected>} />
          <Route path="/app/orgdev" element={<Protected><OrgDev /></Protected>} />
          <Route path="/app/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/app/controller" element={<Protected><Admin /></Protected>} />
          <Route path="/app/master" element={<Protected><MasterDashboard /></Protected>} />
          <Route path="/app/master/library" element={<Protected><MasterLibrary /></Protected>} />
          <Route path="/app/master/orgs" element={<Protected><MasterOrgs /></Protected>} />
          <Route path="/app/me/analytics" element={<Protected><MyAnalytics /></Protected>} />
          <Route path="/app/people/:uid/analytics" element={<Protected><MyAnalytics /></Protected>} />
          <Route path="/app/pulse" element={<Protected><HubPulse /></Protected>} />
          <Route path="/app/trace/:kind/:id" element={<Protected><TracePage /></Protected>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
